import { useEffect, useRef, useState } from "react";
import {
  Search, ChevronLeft, ChevronRight,
  ShieldAlert, Lock, X, Download, FilterX,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DatePicker } from "@/components/ui/date-picker";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";
// Sort tabel (header kolom yang bisa diklik) diangkat ke components/ui/sort.jsx
// (brief owner 19 Agu poin 3, dipakai juga oleh InventoryStok.jsx) — diekspor
// ulang di sini SUPAYA 10 halaman Marketing yang sudah mengimpornya dari
// `_shared.jsx` tidak perlu diubah satu-satu; SATU sumber tetap terjaga karena
// definisinya sendiri sudah tidak ada dua kali (CLAUDE.md: "kalau butuh sesuatu
// di dua tempat, jadikan satu sumber").
export { useSortState, sortRows, SortTh } from "@/components/ui/sort";

/**
 * Potongan bersama seluruh halaman Marketing Fase 1 — dibuat satu berkas
 * supaya konvensi (badge status, paginasi, sort tabel, ekspor CSV, dialog PIN
 * hapus) SATU sumber, bukan disalin 8x per halaman (lihat peringatan
 * "Duplikasi helper melahirkan penyimpangan" di CLAUDE.md).
 *
 * PENYEMBUNYIAN MARGIN (brief bos, kritikal): backend sudah membuang field
 * margin/roi/hpp/modal dari respons untuk user tanpa izin
 * `marketing_margin.view` — field itu TIDAK ADA SAMA SEKALI di objek, bukan
 * bernilai null. `kolomMarginAda()`/`adaField()` di bawah membaca
 * KEBERADAAN key di data asli, `can("marketing_margin","view")` HANYA dipakai
 * sebagai fallback tata letak sebelum data pertama datang. Jangan pernah
 * menghitung ulang margin/ROI di sisi klien dari omzet+modal — itu balik
 * membocorkan angka yang sengaja disembunyikan backend.
 */

// ============ Enum tampilan (mirror enum backend — HANYA label, bukan rumus) ============
export const CHANNELS = ["ads_digital", "kol_endorsement", "promosi_media", "produksi_konten", "lainnya"];
export const CHANNEL_LABEL = {
  ads_digital: "Iklan Digital (Meta/Google/TikTok Ads)",
  kol_endorsement: "Endorsement & KOL",
  promosi_media: "Promosi Media / PFP / Paid Promote",
  produksi_konten: "Produksi Konten",
  lainnya: "Lainnya",
};

export const KOL_PLATFORMS = ["instagram", "tiktok", "youtube", "facebook", "twitter_x", "lainnya"];
export const PFP_PLATFORMS = ["instagram", "tiktok", "twitter_x", "facebook", "telegram", "lainnya"];
export const ADS_PLATFORMS = ["meta", "google", "tiktok", "lainnya"];
export const PLATFORM_LABEL = {
  instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", facebook: "Facebook",
  twitter_x: "Twitter / X", telegram: "Telegram", meta: "Meta (FB/IG Ads)", google: "Google Ads",
  lainnya: "Lainnya",
};

export const DELIVERABLE_JENIS = ["post_feed", "story", "reels", "video", "live", "lainnya"];
export const DELIVERABLE_JENIS_LABEL = {
  post_feed: "Post Feed", story: "Story", reels: "Reels", video: "Video", live: "Live", lainnya: "Lainnya",
};

export const TIPE_DISKON = ["persen", "nominal"];

export const LEAD_STAGES = ["new", "followup", "qualified", "negotiation", "closed_won", "closed_lost"];
export const LEAD_STAGE_LABEL = {
  new: "Baru", followup: "Follow Up", qualified: "Qualified",
  negotiation: "Negosiasi", closed_won: "Deal (Menang)", closed_lost: "Batal (Kalah)",
};
export const LEAD_STAGE_COLOR = {
  new: "border-slate-300 bg-slate-50",
  followup: "border-amber-300 bg-amber-50",
  qualified: "border-sky-300 bg-sky-50",
  negotiation: "border-violet-300 bg-violet-50",
  closed_won: "border-emerald-300 bg-emerald-50",
  closed_lost: "border-rose-300 bg-rose-50",
};

// ============ Status meta (label + warna chip) ============
export const CAMPAIGN_STATUS_META = {
  pending_approval: { label: "Menunggu Approval", chip: "bg-amber-100 text-amber-800" },
  approved: { label: "Disetujui", chip: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Ditolak", chip: "bg-rose-100 text-rose-700" },
};

export const DEAL_STATUS_META = {
  pending_approval: { label: "Menunggu Approval", chip: "bg-amber-100 text-amber-800" },
  approved: { label: "Disetujui", chip: "bg-sky-100 text-sky-700" },
  berjalan: { label: "Berjalan", chip: "bg-sky-100 text-sky-700" },
  selesai: { label: "Selesai", chip: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Ditolak", chip: "bg-rose-100 text-rose-700" },
};

export const DP_STATUS_META = {
  tidak_ada: { label: "Tanpa DP", chip: "bg-slate-100 text-slate-500" },
  belum_dibayar: { label: "DP Belum Dibayar", chip: "bg-amber-100 text-amber-800" },
  diproses: { label: "DP Diproses", chip: "bg-sky-100 text-sky-700" },
  dibayar: { label: "DP Dibayar", chip: "bg-emerald-100 text-emerald-800" },
};

export const PELUNASAN_STATUS_META = {
  belum_dibayar: { label: "Belum Lunas", chip: "bg-amber-100 text-amber-800" },
  diproses: { label: "Diproses", chip: "bg-sky-100 text-sky-700" },
  dibayar: { label: "Lunas", chip: "bg-emerald-100 text-emerald-800" },
};

export const DELIVERABLE_STATUS_META = {
  pending: { label: "Pending", chip: "bg-slate-100 text-slate-600" },
  submitted: { label: "Disubmit", chip: "bg-sky-100 text-sky-700" },
  approved: { label: "Diverifikasi", chip: "bg-emerald-100 text-emerald-800" },
  late: { label: "Telat", chip: "bg-rose-100 text-rose-700" },
};

export const PLACEMENT_STATUS_META = {
  rencana: { label: "Rencana", chip: "bg-slate-100 text-slate-600" },
  tayang: { label: "Tayang", chip: "bg-sky-100 text-sky-700" },
  selesai: { label: "Selesai (Dibayar)", chip: "bg-emerald-100 text-emerald-800" },
  dibatalkan: { label: "Dibatalkan", chip: "bg-rose-100 text-rose-700" },
};

export function StatusBadge({ meta, value, className = "" }) {
  const m = meta[value] || { label: value || "-", chip: "bg-slate-100 text-slate-600" };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${m.chip} ${className}`}>
      {m.label}
    </span>
  );
}

// ============ Header halaman ============
export function PageHeader({ kicker = "Marketing", icon: Icon, title, desc, actions }) {
  return (
    <div className="eh-page-heading flex items-start justify-between flex-col lg:flex-row gap-3">
      <div>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">{kicker}</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 inline-flex items-center gap-2">
          {Icon && <Icon className="w-7 h-7 text-blue-500" />} {title}
        </h1>
        {desc && <p className="text-sm text-slate-600 mt-1 max-w-2xl">{desc}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

/** Halaman utuh tanpa izin `view` — pola yang sama dengan MultiRekon.jsx/Retur.jsx. */
export function TidakPunyaAkses({ label, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid={testid}>
      <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
      <p className="text-slate-700 font-medium">Anda tidak punya akses ke {label}</p>
      <p className="text-sm text-slate-500 mt-1">Hubungi admin untuk meminta izin melihat modul ini.</p>
    </div>
  );
}

// ============ Pencarian ============
export function SearchInput({ value, onChange, placeholder = "Cari...", testid, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
             className="h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm bg-white text-slate-700 w-full focus:outline-none focus:ring-2 focus:ring-emerald-500" />
    </div>
  );
}

// ============ Toolbar filter (SATU sumber untuk 4 halaman daftar) ============
/**
 * SEMUA filter di halaman Marketing dikerjakan DI SERVER, bukan dengan
 * menyaring array yang sudah terunduh. Alasannya ada di CLAUDE.md: keempat
 * daftar dipotong `to_list(2000..5000)` di backend, jadi menyaring hasil yang
 * sudah terpotong membuat baris ke-N+1 tidak akan pernah ketemu — dan layarnya
 * melapor "tidak ada hasil" dengan yakin, tidak bisa dibedakan dari "memang
 * tidak ada".
 *
 * Komponen-komponen di bawah diangkat ke sini karena bentuk toolbar-nya sama
 * di Leads/Campaigns/Ads/PromoCodes; pemilih template pernah tersalin di 6
 * berkas dan berpisah jalan (peringatan "Duplikasi helper" di CLAUDE.md).
 */

/** Buang nilai kosong sebelum dikirim sebagai query params — `?q=` kosong
 *  tetap sampai ke backend sebagai string kosong dan bikin cache key beda
 *  tanpa alasan. */
export function paramFilter(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    out[k] = v;
  });
  return out;
}

/** Dropdown filter generik. `opsi` = array string ATAU array {value,label}. */
export function FilterSelect({ value, onChange, opsi, semuaLabel, testid, className = "" }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className={`h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white text-slate-700 max-w-[16rem] ${className}`}>
      <option value="">{semuaLabel}</option>
      {(opsi || []).map((o) => {
        const val = typeof o === "string" ? o : o.value;
        const lbl = typeof o === "string" ? o : o.label;
        return <option key={val} value={val}>{lbl}</option>;
      })}
    </select>
  );
}

/** Sepasang DatePicker "dari s/d sampai". Nilainya dikirim apa adanya ke
 *  server (YYYY-MM-DD) — server yang menolak format salah dengan 400, supaya
 *  tanggal ngawur tidak diam-diam jadi daftar kosong. */
export function FilterRentangTanggal({ dari, sampai, onDari, onSampai, testid,
                                       labelDari = "Dari tanggal", labelSampai = "Sampai tanggal" }) {
  return (
    <div className="flex items-center gap-1.5">
      <DatePicker value={dari} onChange={onDari} placeholder={labelDari}
                  data-testid={`${testid}-dari`} className="w-36" />
      <span className="text-slate-400 text-xs">s/d</span>
      <DatePicker value={sampai} onChange={onSampai} placeholder={labelSampai}
                  data-testid={`${testid}-sampai`} className="w-36" />
    </div>
  );
}

/** Muncul HANYA saat ada filter aktif — tombol yang selalu terlihat tapi tidak
 *  pernah berguna cuma menambah beban baca toolbar. */
export function TombolResetFilter({ aktif, onReset, testid }) {
  if (!aktif) return null;
  return (
    <button onClick={onReset} data-testid={testid}
            className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-600 inline-flex items-center gap-1.5">
      <FilterX className="w-3.5 h-3.5" /> Reset Filter
    </button>
  );
}

/**
 * Opsi dropdown filter (sumber/cabang/pembuat) dari endpoint `.../opsi-filter`.
 *
 * DIAMBIL DARI SERVER, bukan diturunkan dari baris yang sedang tampil: daftar
 * yang tampil sudah dipotong dan sudah tersaring, jadi opsi yang diturunkan
 * darinya akan kehilangan pilihan yang justru sedang dicari pengguna — dan
 * lebih buruk, pilihan yang sedang aktif akan lenyap dari dropdown-nya sendiri.
 */
export function useOpsiFilter(url, aktif = true) {
  const [opsi, setOpsi] = useState({});
  useEffect(() => {
    if (!aktif) return undefined;
    let hidup = true;
    api.get(url).then((r) => { if (hidup) setOpsi(r.data || {}); }).catch(() => {});
    return () => { hidup = false; };
  }, [url, aktif]);
  return opsi;
}

/** True kalau salah satu nilai filter terisi — untuk menyalakan tombol reset. */
export function adaFilterAktif(...nilai) {
  return nilai.some((v) => v !== undefined && v !== null && v !== "" && v !== false);
}

// ============ Paginasi client-side ============
export function usePaginasi(totalItems, pageSize = 15) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);
  const awal = (page - 1) * pageSize;
  return { page, setPage, totalPages, awal, akhir: awal + pageSize, pageSize };
}

export function Paginasi({ page, setPage, totalPages, testid }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 px-4 py-3 text-xs text-slate-600 border-t border-slate-100">
      <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid={`${testid}-prev`}
              className="w-8 h-8 rounded-lg border border-slate-200 grid place-items-center disabled:opacity-40 hover:bg-slate-50">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span data-testid={`${testid}-info`}>Halaman {page} dari {totalPages}</span>
      <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid={`${testid}-next`}
              className="w-8 h-8 rounded-lg border border-slate-200 grid place-items-center disabled:opacity-40 hover:bg-slate-50">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============ Ekspor CSV (murni sisi klien — daftar yang sudah tersaring/terurut di layar) ============
function csvEscape(v) {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** kolom: [{ label, value: (row) => string|number }] */
export function unduhCsv(namaBerkas, kolom, baris) {
  const header = kolom.map((k) => csvEscape(k.label)).join(",");
  const lines = baris.map((row) => kolom.map((k) => csvEscape(k.value(row))).join(","));
  // BOM UTF-8 di depan supaya Excel Windows tidak salah baca simbol "Rp"/titik ribuan.
  const csv = "﻿" + [header, ...lines].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = namaBerkas;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

export function TombolEksporCsv({ onClick, testid, label = "Ekspor CSV" }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5">
      <Download className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

// ============ Modal form generik ============
// KENAPA hook dipanggil SEBELUM `if (!open) return null`: ke-20 titik panggil
// di 11 halaman Marketing merender `<Modal open={!!form} ...>` TERUS-MENERUS
// (bukan `{form && <Modal>}`) — instance komponennya tetap ter-mount lintas
// buka/tutup, `open` yang berubah. Rules of Hooks mewajibkan hook dipanggil
// di urutan yang sama tiap render, jadi `useModalGuard` (dan `active={open}`
// di dalamnya) harus di atas early-return, bukan di bawahnya.
//
// `onSubmit`/`busy`/`disabled`/`dangerous` opsional, TIDAK dipakai pemanggil
// manapun saat ini (semua 20 memakai `footer` render-prop dengan tombol
// Simpan sendiri) — kalau nanti satu halaman ingin menandai modalnya
// `dangerous`, tinggal tambah prop itu di titik panggil, tidak perlu ubah
// berkas ini lagi. Ctrl+Enter tetap jalan lewat `footerRef` (klik tombol
// terakhir di footer, lihat useModalGuard.jsx) walau `onSubmit` tidak dioper.
export function Modal({ open, onClose, title, children, footer, maxWidth = "max-w-lg", testid, onSubmit, busy, disabled, dangerous }) {
  const footerRef = useRef(null);
  const guard = useModalGuard({ onClose, onSubmit, busy, disabled, dangerous, active: open, footerRef });
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={guard.requestClose} data-testid={testid}>
      <div ref={guard.contentRef} className={`bg-white rounded-2xl shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <p className="font-display font-bold text-base text-slate-900">{title}</p>
          <button onClick={guard.requestClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
        {guard.confirming && (
          <div className="px-5 pb-4">
            <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid={testid ? `${testid}-guard` : "modal-guard-bar"} />
          </div>
        )}
        {footer && <div ref={footerRef} className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export const inputCls =
  "h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white";
export const selectCls = inputCls;
export const textareaCls =
  "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";

/** Kombo input teks + datalist — dipakai untuk field brand/cabang (string bebas
 *  yang IDEALNYA salah satu dari daftar cabang, tapi TIDAK divalidasi ketat di
 *  backend — lihat routers/marketing/_shared.py poin 1). */
export function InputCabang({ value, onChange, opsi, testid, placeholder = "Nama cabang/brand", className }) {
  const listId = `dl-${testid}`;
  return (
    <>
      <input value={value || ""} list={listId} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             data-testid={testid} className={className || inputCls} />
      <datalist id={listId}>{(opsi || []).map((o) => <option key={o} value={o} />)}</datalist>
    </>
  );
}

// ============ PIN persetujuan hapus (brief #23 — pola sama InputNota.jsx) ============
export function usePinHapus() {
  const [dialog, setDialog] = useState(null); // {pin, submitting, error, retry}

  /** aksiHapus: (pin) => Promise — panggilan DELETE sungguhan.
   *  Kembalikan true kalau langsung sukses tanpa PIN, false kalau dialog PIN dibuka/gagal. */
  const hapusDenganPin = async (aksiHapus, { modul, targetId, ringkasan }) => {
    try {
      await aksiHapus(undefined);
      return true;
    } catch (e) {
      if (e?.response?.status === 428) {
        try {
          await api.post("/aktivitas/hapus/ajukan", { modul, target_id: targetId, ringkasan });
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Gagal mengajukan izin hapus");
          return false;
        }
        setDialog({ pin: "", submitting: false, error: "", retry: (pin) => aksiHapus(pin) });
        return false;
      }
      toast.error(e?.response?.data?.detail || "Gagal menghapus data");
      return false;
    }
  };

  const submitDialog = async (onSukses) => {
    if (!dialog) return;
    setDialog((d) => ({ ...d, submitting: true, error: "" }));
    try {
      await dialog.retry(dialog.pin);
      setDialog(null);
      onSukses?.();
    } catch (e) {
      if (e?.response?.status === 403) {
        setDialog((d) => ({ ...d, submitting: false, error: "PIN salah atau sudah kedaluwarsa — coba lagi." }));
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menghapus data");
        setDialog(null);
      }
    }
  };

  return { dialog, setDialog, hapusDenganPin, submitDialog };
}

export function PinHapusDialog({ dialog, setDialog, onSubmit }) {
  if (!dialog) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setDialog(null)}>
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="mkt-pin-dialog">
        <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
          <h3 className="font-semibold text-sm inline-flex items-center gap-1.5"><Lock className="w-4 h-4" /> PIN Persetujuan Hapus</h3>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-600">
            Penghapusan butuh persetujuan. Minta PIN ke pemegang izin (lihat Pusat Approval), lalu masukkan di sini.
          </p>
          <input value={dialog.pin}
                 onChange={(e) => setDialog((d) => ({ ...d, pin: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
                 maxLength={6} inputMode="numeric" placeholder="6 digit PIN" autoFocus
                 data-testid="mkt-pin-input"
                 className="w-full h-11 px-3 rounded-lg border border-slate-200 text-center text-lg tracking-[0.3em] font-mono" />
          {dialog.error && <p className="text-xs text-rose-600">{dialog.error}</p>}
        </div>
        <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
          <button onClick={() => setDialog(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={onSubmit} disabled={dialog.submitting || dialog.pin.length !== 6}
                  data-testid="mkt-pin-kirim"
                  className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium">
            {dialog.submitting ? "Memproses..." : "Kirim & Hapus"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Penyembunyian margin — bantu keputusan tampil kolom/kartu ============
/** Field ADA di objek (walau nilainya null) — beda dengan `obj[field]` yang
 *  juga true untuk `undefined`. Dipakai memutuskan render kolom margin/ROI:
 *  backend meng-hapus KEY-nya sepenuhnya untuk user tanpa izin, jadi "ada key"
 *  = sumber kebenaran, BUKAN `can("marketing_margin","view")` semata. */
export function adaField(obj, field) {
  return obj != null && Object.prototype.hasOwnProperty.call(obj, field);
}

/** Dipakai di level KOLOM tabel (satu keputusan untuk seluruh tabel): baca
 *  bentuk baris pertama yang sungguh datang dari API; kalau belum ada data
 *  sama sekali, jatuh ke `can("marketing_margin","view")` HANYA untuk
 *  menghindari layout "kedip" sebelum fetch pertama selesai. */
export function kolomMarginAda(rows, field, marginAllowedFallback) {
  if (Array.isArray(rows) && rows.length > 0) return adaField(rows[0], field);
  return !!marginAllowedFallback;
}

// ============ Akun kas/bank/petty cash (sumber pembayaran DP/pelunasan/placement) ============
// GET terbuka untuk siapa pun yang login (get_current_user, bukan
// require_permission) — sama seperti dipakai FinanceUtang.jsx untuk modal bayar.
export function useAkunKasBank() {
  const [akun, setAkun] = useState([]);
  useEffect(() => {
    let hidup = true;
    api.get("/finance/accounts/kas-bank").then((r) => { if (hidup) setAkun(r.data || []); }).catch(() => {});
    return () => { hidup = false; };
  }, []);
  return akun;
}

/** Daftar campaign — dipakai untuk select & join tampilan nama/brand di
 *  halaman lain (Ads/PFP/Leads/Promo). HANYA di-fetch kalau user punya izin
 *  `marketing_campaign.view` (rule #1 CLAUDE.md: jangan panggil endpoint yang
 *  ujungnya 403) — kalau tidak, campaign_id ditampilkan apa adanya (kode
 *  singkat) tanpa nama. */
export function useCampaignMap(bolehLihatCampaign) {
  const [map, setMap] = useState({});
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!bolehLihatCampaign) return;
    let hidup = true;
    api.get("/marketing/campaigns").then((r) => {
      if (!hidup) return;
      const rows = r.data || [];
      const m = {};
      rows.forEach((c) => { m[c.id] = c; });
      setMap(m);
      setList(rows);
    }).catch(() => {});
    return () => { hidup = false; };
  }, [bolehLihatCampaign]);
  return { campaignMap: map, campaignList: list };
}

export function namaCampaign(campaignMap, campaignId) {
  if (!campaignId) return "-";
  const c = campaignMap[campaignId];
  if (!c) return campaignId.slice(0, 8) + "…";
  return `${c.code ? c.code + " — " : ""}${c.nama}`;
}

export function fmtPersen(v) {
  if (v === null || v === undefined || v === "") return "-";
  return `${v}%`;
}
